import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import './CelpipReadingPractice.css';

const CelpipReadingPractice = () => {
    const navigate = useNavigate();
    const reduxUser = useSelector((state) => state.auth?.authData);
    const observerRef = useRef(null);

    // Normalize user data
    const getStoredUser = () => {
        if (reduxUser?.result) return reduxUser.result;
        const localUser = JSON.parse(localStorage.getItem('user_info'));
        if (localUser?.result) return localUser.result;
        return null;
    };

    const handleStartPractice = () => {
        const currentUser = getStoredUser();
        if (currentUser) {
            navigate('/reading');
        } else {
            navigate('/login');
        }
    };

    const handleTestClick = (testNumber) => {
        const currentUser = getStoredUser();
        if (currentUser) {
            navigate(`/reading/${testNumber}`);
        } else {
            navigate('/login');
        }
    };

    // Intersection Observer for scroll reveal
    const initObserver = useCallback(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('rp-visible');
                    }
                });
            },
            { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
        );

        const elements = document.querySelectorAll(
            '.rp-task-card, .rp-step-item, .rp-feature-card, .rp-test-item, .rp-tip-card'
        );
        elements.forEach((el, index) => {
            el.style.transitionDelay = `${index * 0.08}s`;
            observerRef.current.observe(el);
        });
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
        // Slight delay to ensure DOM is ready
        const timer = setTimeout(() => initObserver(), 100);
        return () => {
            clearTimeout(timer);
            if (observerRef.current) observerRef.current.disconnect();
        };
    }, [initObserver]);

    // Reading task data
    const readingTasks = [
        {
            number: 1,
            title: 'Reading Correspondence',
            desc: 'Read and comprehend emails, letters, or messages. Answer questions about tone, purpose, and details.',
            questions: '8 Questions',
            time: '~11 min',
        },
        {
            number: 2,
            title: 'Reading to Apply a Diagram',
            desc: 'Match information from a text to a diagram, chart, or visual. Tests your ability to organize information.',
            questions: '7 Questions',
            time: '~9 min',
        },
        {
            number: 3,
            title: 'Reading for Information',
            desc: 'Read longer texts and answer detailed comprehension questions about facts, inferences, and vocabulary.',
            questions: '9 Questions',
            time: '~10 min',
        },
        {
            number: 4,
            title: 'Reading for Viewpoints',
            desc: 'Identify opinions, attitudes, and viewpoints from multiple writers or sources. Distinguish perspectives.',
            questions: '8 Questions',
            time: '~10 min',
        },
    ];

    const features = [
        {
            icon: '🎯',
            title: 'Real Exam Format',
            desc: 'Practice with questions that mirror the actual CELPIP Reading test format and difficulty level.',
        },
        {
            icon: '⚡',
            title: 'Instant Scoring',
            desc: 'Get your results immediately after completing each test with detailed answer explanations.',
        },
        {
            icon: '📊',
            title: 'Progress Tracking',
            desc: 'Track your improvement over time with comprehensive analytics and performance insights.',
        },
        {
            icon: '🤖',
            title: 'AI-Powered Feedback',
            desc: 'Receive intelligent feedback on your weak areas and personalized study recommendations.',
        },
        {
            icon: '📚',
            title: '50+ Practice Tests',
            desc: 'Access a vast library of reading practice tests covering all four task types thoroughly.',
        },
        {
            icon: '⏱️',
            title: 'Timed Practice',
            desc: 'Build exam-day stamina with realistic timer settings that match actual test conditions.',
        },
    ];

    const practiceTests = [
        { num: 1, name: 'Reading Practice Test 1', tasks: 'All 4 Tasks · 32 Questions', difficulty: 'easy' },
        { num: 2, name: 'Reading Practice Test 2', tasks: 'All 4 Tasks · 32 Questions', difficulty: 'easy' },
        { num: 3, name: 'Reading Practice Test 3', tasks: 'All 4 Tasks · 32 Questions', difficulty: 'medium' },
        { num: 4, name: 'Reading Practice Test 4', tasks: 'All 4 Tasks · 32 Questions', difficulty: 'medium' },
        { num: 5, name: 'Reading Practice Test 5', tasks: 'All 4 Tasks · 32 Questions', difficulty: 'hard' },
        { num: 6, name: 'Reading Practice Test 6', tasks: 'All 4 Tasks · 32 Questions', difficulty: 'hard' },
    ];

    const tips = [
        {
            icon: '🔍',
            title: 'Skim Before You Read',
            desc: 'Quickly scan headings, topic sentences, and keywords before diving deep. This gives you a mental map of the passage.',
        },
        {
            icon: '⏰',
            title: 'Manage Your Time',
            desc: "Strict per-part timers: Part 1 (11 min), Part 2 (9 min), Part 3 (10 min), Part 4 (13 min). There is NO back button between parts, so you cannot save or carry forward unused time.",
        },
        {
            icon: '📝',
            title: 'Eliminate Wrong Answers',
            desc: 'Use the process of elimination. Cross out clearly wrong options to increase your odds on tricky questions.',
        },
        {
            icon: '🧠',
            title: 'Focus on Keywords',
            desc: 'Identify keywords in both the question and the passage. Match them to find the correct answer quickly.',
        },
    ];

    const steps = [
        {
            title: 'Choose Your Test',
            desc: 'Select from our library of practice tests organized by difficulty — start easy and build up to harder challenges.',
        },
        {
            title: 'Read & Answer',
            desc: 'Work through realistic reading passages and answer multiple-choice questions just like the real CELPIP exam.',
        },
        {
            title: 'Review Results',
            desc: 'See your score instantly, review correct answers with detailed explanations, and understand your mistakes.',
        },
        {
            title: 'Track & Improve',
            desc: 'Monitor your progress over time, identify weak areas, and use AI-powered recommendations to improve faster.',
        },
    ];

    return (
        <div className="rp-landing-wrapper">
            {/* Ambient background glow */}
            <div className="rp-ambient-glow" />

            {/* ── HERO SECTION ── */}
            <section className="rp-hero" id="rp-hero">
                <div className="rp-hero-bg-pattern" />
                <div className="rp-hero-content">
                    <div className="rp-hero-text">
                        <div className="rp-hero-badge">
                            <span className="rp-hero-badge-dot" />
                            CELPIP READING MODULE
                        </div>
                        <h1 className="rp-hero-title">
                            Master Your{' '}
                            <span className="rp-hero-title-gradient">
                                CELPIP Reading
                            </span>{' '}
                            Score
                        </h1>
                        <p className="rp-hero-subtitle">
                            Practice with 50+ realistic reading tests, get instant AI-powered
                            feedback, and track your progress — all designed to help you
                            score CLB 9+ on your CELPIP Reading module.
                        </p>
                        <div className="rp-hero-actions">
                            <button
                                className="rp-btn-primary"
                                onClick={handleStartPractice}
                                id="rp-start-practice-hero"
                            >
                                <span className="rp-btn-icon">📖</span>
                                Start Free Practice
                            </button>
                            <button
                                className="rp-btn-secondary"
                                onClick={() => {
                                    document.getElementById('rp-tasks-section')?.scrollIntoView({ behavior: 'smooth' });
                                }}
                                id="rp-explore-tasks"
                            >
                                Explore Tasks
                                <span className="rp-btn-icon">↓</span>
                            </button>
                        </div>
                    </div>

                    {/* Hero floating cards illustration */}
                    <div className="rp-hero-visual">
                        <div className="rp-hero-card-stack">
                            {/* Card 1 – Reading Passage Preview */}
                            <div className="rp-hero-float-card">
                                <div className="rp-float-card-label">Task 1 · Reading</div>
                                <div className="rp-float-card-title">
                                    Reading Correspondence
                                </div>
                                <div className="rp-float-card-text">
                                    "Dear Mr. Thompson, I am writing to confirm your appointment with the immigration office..."
                                </div>
                                <span className="rp-float-card-tag">✓ 8 Questions</span>
                            </div>

                            {/* Card 2 – Score Preview */}
                            <div className="rp-hero-float-card">
                                <div className="rp-float-card-label">Your Score</div>
                                <div className="rp-float-card-title">CLB Level 9</div>
                                <div className="rp-float-card-score">
                                    <div className="rp-score-bar">
                                        <div
                                            className="rp-score-bar-fill"
                                            style={{ '--fill-width': '85%' }}
                                        />
                                    </div>
                                    <span className="rp-score-value">85%</span>
                                </div>
                            </div>

                            {/* Card 3 – Streak */}
                            <div className="rp-hero-float-card">
                                <div className="rp-float-card-label">Practice Streak</div>
                                <div className="rp-float-card-title">🔥 7 Days</div>
                                <div className="rp-float-card-text">
                                    You're on fire! Keep practicing daily.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── READING TASKS OVERVIEW ── */}
            <section className="rp-tasks-section" id="rp-tasks-section">
                <div className="rp-section-header">
                    <div className="rp-section-eyebrow">Reading Module Breakdown</div>
                    <h2 className="rp-section-title">4 Tasks, One Goal: Your Best Score</h2>
                    <p className="rp-section-subtitle">
                        The CELPIP Reading test has four distinct tasks. Master each one with
                        targeted practice and detailed explanations.
                    </p>
                </div>
                <div className="rp-tasks-grid">
                    {readingTasks.map((task) => (
                        <div
                            key={task.number}
                            className="rp-task-card"
                            onClick={() => handleStartPractice()}
                        >
                            <div className="rp-task-card-number">{task.number}</div>
                            <h3 className="rp-task-card-title">{task.title}</h3>
                            <p className="rp-task-card-desc">{task.desc}</p>
                            <div className="rp-task-card-meta">
                                <span className="rp-task-meta-item">
                                    📝 {task.questions}
                                </span>
                                <span className="rp-task-meta-item">
                                    ⏱️ {task.time}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── HOW IT WORKS ── */}
            <section className="rp-how-section">
                <div className="rp-how-content">
                    <div className="rp-section-header">
                        <div className="rp-section-eyebrow">How It Works</div>
                        <h2 className="rp-section-title">Simple Steps to Success</h2>
                        <p className="rp-section-subtitle">
                            Our proven 4-step approach helps thousands of students improve
                            their reading scores every month.
                        </p>
                    </div>
                    <div className="rp-steps-timeline">
                        {steps.map((step, i) => (
                            <div key={i} className="rp-step-item">
                                <div className="rp-step-number">{i + 1}</div>
                                <div className="rp-step-body">
                                    <h3 className="rp-step-title">{step.title}</h3>
                                    <p className="rp-step-desc">{step.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── FEATURES ── */}
            <section className="rp-features-section">
                <div className="rp-section-header">
                    <div className="rp-section-eyebrow">Why Engdemy</div>
                    <h2 className="rp-section-title">Everything You Need to Excel</h2>
                    <p className="rp-section-subtitle">
                        Our platform is built specifically for CELPIP success with tools
                        and resources you won't find anywhere else.
                    </p>
                </div>
                <div className="rp-features-grid">
                    {features.map((feature, i) => (
                        <div key={i} className="rp-feature-card">
                            <div className="rp-feature-icon">{feature.icon}</div>
                            <h3 className="rp-feature-title">{feature.title}</h3>
                            <p className="rp-feature-desc">{feature.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── PRACTICE TESTS ── */}
            <section className="rp-tests-section">
                <div className="rp-tests-content">
                    <div className="rp-section-header">
                        <div className="rp-section-eyebrow">Practice Tests</div>
                        <h2 className="rp-section-title">Jump Into a Practice Test</h2>
                        <p className="rp-section-subtitle">
                            Choose a test below to start practicing. Each test includes all
                            four reading tasks in exam format.
                        </p>
                    </div>
                    <div className="rp-tests-list">
                        {practiceTests.map((test) => (
                            <div
                                key={test.num}
                                className="rp-test-item"
                                onClick={() => handleTestClick(test.num)}
                            >
                                <div className="rp-test-item-left">
                                    <div className="rp-test-item-num">{test.num}</div>
                                    <div className="rp-test-item-info">
                                        <h4>{test.name}</h4>
                                        <p>{test.tasks}</p>
                                    </div>
                                </div>
                                <div className="rp-test-item-right">
                                    <span className={`rp-test-difficulty ${test.difficulty}`}>
                                        {test.difficulty.charAt(0).toUpperCase() + test.difficulty.slice(1)}
                                    </span>
                                    <span className="rp-test-arrow">→</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── TIPS & STRATEGIES ── */}
            <section className="rp-tips-section">
                <div className="rp-section-header">
                    <div className="rp-section-eyebrow">Pro Tips</div>
                    <h2 className="rp-section-title">Reading Strategies That Work</h2>
                    <p className="rp-section-subtitle">
                        Apply these proven strategies to boost your reading score and manage
                        your time effectively on test day.
                    </p>
                </div>
                <div className="rp-tips-grid">
                    {tips.map((tip, i) => (
                        <div key={i} className="rp-tip-card">
                            <div className="rp-tip-icon">{tip.icon}</div>
                            <div className="rp-tip-content">
                                <h4>{tip.title}</h4>
                                <p>{tip.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── CTA BANNER ── */}
            <section className="rp-cta-section">
                <div className="rp-cta-card">
                    <h2 className="rp-cta-title">
                        Ready to Ace Your Reading?
                    </h2>
                    <p className="rp-cta-subtitle">
                        Join thousands of test-takers who improved their CELPIP reading score
                        with our AI-powered practice platform.
                    </p>
                    <div className="rp-cta-actions">
                        <button
                            className="rp-btn-primary"
                            onClick={handleStartPractice}
                            id="rp-start-practice-cta"
                        >
                            <span className="rp-btn-icon">🚀</span>
                            Start Practicing Now
                        </button>
                        <button
                            className="rp-btn-secondary"
                            onClick={() => navigate('/celpip')}
                            id="rp-explore-celpip"
                        >
                            Explore All Modules
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CelpipReadingPractice;
